import Typography from '@alltrails/core/components/Typography';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import type Plan from '@alltrails/core/types/Plan';
import type Coupon from '@alltrails/types/Coupon';
import type SubscriptionTier from '@alltrails/core/types/SubscriptionTier';
import { getPriceTotal, isTrial } from '../../utils/plan';

type PaymentFinePrintTextProps = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  isCouponApplied: boolean;
  isComplianceCheckboxEnabled?: boolean;
  plan: Plan;
  subscriptionTier: SubscriptionTier;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=637%3A55454
function PaymentFinePrintText({
  currencyCode,
  coupon,
  isComplianceCheckboxEnabled,
  isCouponApplied,
  plan,
  subscriptionTier
}: PaymentFinePrintTextProps) {
  const months = plan.numberOfMonths;
  const totalCost = <FormattedNumber currency={currencyCode} style="currency" value={getPriceTotal(plan, coupon)} />;
  const baseAnnualCost = <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />;

  if (subscriptionTier === 'plus' && !isCouponApplied) {
    if (isComplianceCheckboxEnabled) {
      return (
        <FormattedMessage
          defaultMessage="I agree to be charged {cost} for an annual subscription to Peak. If I already paid for Plus, I’ll be credited the remaining value towards my new plan. If I’m in a Plus free trial, I’ll get Peak access now and will be charged {cost} when it ends. My Peak plan renews yearly using this payment method unless I turn off auto-renew under Account settings at least 1 day before my trial ends or my renewal date. I’ll be notified of any price changes."
          values={{ cost: totalCost }}
        />
      );
    }
    return (
      <FormattedMessage
        defaultMessage="If you paid for Plus, you’ll be charged {cost} today and refunded the remainder of your subscription. If you’re in a Plus free trial, you’ll get Peak access now and be charged {cost} when the trial ends. Your Peak plan will renew in {months} months unless you change your settings."
        values={{ cost: totalCost, months }}
      />
    );
  }

  if (isTrial(plan, coupon)) {
    if (isComplianceCheckboxEnabled) {
      return (
        <FormattedMessage
          defaultMessage="I agree to be charged {cost} after my free trial. My plan renews yearly with this payment method unless I turn off auto-renew under Account settings at least 1 day before my trial ends or my renewal date. I’ll be notified of any price changes."
          values={{ cost: totalCost }}
        />
      );
    }

    // always multiple months here so we don't need singular form
    return (
      <FormattedMessage
        defaultMessage="You will be charged {baseAnnualCost} when your free trial ends and your subscription will automatically renew every {months} months unless you turn off your auto-renewal within your profile settings prior to your renewal date."
        values={{ baseAnnualCost, months }}
      />
    );
  }

  if (isCouponApplied) {
    const couponAnnualCost = coupon?.duration === 'forever' ? totalCost : baseAnnualCost;

    if (subscriptionTier === 'plus') {
      if (isComplianceCheckboxEnabled) {
        return (
          <FormattedMessage
            defaultMessage="I agree to be charged {cost} for an annual subscription to Peak. If I already paid for Plus, I’ll be credited the remaining value towards my new plan. If I’m in a free Plus trial, I’ll get Peak access now and will be charged when it ends. My Peak plan renews yearly at {baseAnnualCost} using this payment method unless I turn off auto-renew under Account settings at least 1 day before my trial ends or my renewal date. I’ll be notified of any price changes."
            values={{ cost: totalCost, baseAnnualCost: couponAnnualCost }}
          />
        );
      }

      return (
        <FormattedMessage
          defaultMessage="If you paid for Plus, you’ll be charged {cost} today and refunded the remainder of your subscription. If you’re in a Plus free trial, you’ll get Peak access now and be charged {cost} when the trial ends. Your Peak plan will automatically renew after {months} months at {baseAnnualCost} unless you update your renewal settings."
          values={{ cost: totalCost, baseAnnualCost: couponAnnualCost, months }}
        />
      );
    }

    if (isComplianceCheckboxEnabled) {
      return (
        <FormattedMessage
          defaultMessage="I agree to be charged {cost} today with yearly auto-renewal at {baseAnnualCost} using this payment method unless I turn off auto-renew under Account settings at least 1 day before my renewal date. I’ll be notified of any price changes."
          values={{ cost: totalCost, baseAnnualCost: couponAnnualCost }}
        />
      );
    }

    return (
      <FormattedMessage
        defaultMessage="You will be charged {cost} today and automatically renew after {months} months at {baseAnnualCost} unless you update your renewal settings."
        values={{ cost: totalCost, baseAnnualCost: couponAnnualCost, months }}
      />
    );
  }

  if (isComplianceCheckboxEnabled) {
    return (
      <FormattedMessage
        defaultMessage="I agree to be charged {cost} today with yearly auto-renewal with this payment method unless I turn off auto-renew under Account settings at least 1 day before my renewal date. I’ll be notified of any price changes."
        values={{ cost: totalCost }}
      />
    );
  }

  return (
    <FormattedMessage
      defaultMessage="You will be charged {cost} today and automatically renew after {months} months unless you update your renewal settings."
      values={{ cost: totalCost, months }}
    />
  );
}

type Props = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  isCouponApplied: boolean;
  isComplianceCheckboxEnabled?: boolean;
  plan: Plan;
  subscriptionTier: SubscriptionTier;
};

export default function PaymentFinePrint({
  currencyCode,
  coupon,
  isComplianceCheckboxEnabled = false,
  isCouponApplied,
  plan,
  subscriptionTier
}: Props) {
  return (
    <Typography color="secondary" variant="paragraph100">
      <PaymentFinePrintText
        coupon={coupon}
        currencyCode={currencyCode}
        isCouponApplied={isCouponApplied}
        isComplianceCheckboxEnabled={isComplianceCheckboxEnabled}
        plan={plan}
        subscriptionTier={subscriptionTier}
      />
    </Typography>
  );
}
